import { Loader2 } from 'lucide-react';

import { useData } from '@virtual-care/hooks';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@virtual-care/shadcn';
import { FieldMessage } from '@virtual-care/ui';

interface PrintingDoctorSelectProps<
  TF extends {
    state: any;
    handleChange: (value: string) => void;
  },
> {
  field: TF;
  printingRequestId: string;
  label?: string;
}

export function PrintingDoctorSelect<
  TF extends {
    state: any;
    handleChange: (value: string) => void;
  },
>({ field, printingRequestId, label }: PrintingDoctorSelectProps<TF>) {
  const doctorsQuery = useData(
    'GET /printing-requests/{id}/doctors',
    { id: printingRequestId },
    { enabled: !!printingRequestId },
  );

  return (
    <div className="space-y-2">
      <Label htmlFor="doctorId">{label || 'Select Doctor'}</Label>
      {doctorsQuery.isLoading ? (
        <div className="text-muted-foreground text-sm">Loading doctors...</div>
      ) : doctorsQuery.data?.length === 0 ? (
        <div className="text-muted-foreground text-sm">
          No doctors available for the 3D Printing speciality
        </div>
      ) : (
        <Select
          value={field.state.value}
          onValueChange={(value) => field.handleChange(value)}
        >
          <SelectTrigger className="w-full">
            <div className="flex items-center gap-x-1">
              {doctorsQuery.isPending && <Loader2 className="animate-spin" />}
              <SelectValue placeholder="Choose a doctor" />
            </div>
          </SelectTrigger>
          <SelectContent>
            {doctorsQuery.data?.map((doctor) => (
              <SelectItem key={doctor.id} value={doctor.id}>
                {doctor.firstName} {doctor.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <FieldMessage errors={field.state.meta.errors} />
    </div>
  );
}
